import BaseModel from './BaseModel';
import * as AT from '../../components/container/attributes';
import Resource from './Resource';
import FileDocument from './FileDocument';
import ProjectPart from './ProjectPart';
import {HitOnlineEnums} from 'hit-online-web-ui/src/helpers/constants';
import {translate} from '../../plugins/i18n/i18n';
import {HitDateUtils} from '@hit/components/src/utils/date/HitDateUtils';

export default class ProjectPartSpecSheet extends BaseModel {
  constructor({
    id,
    projectPart = undefined,
    document = undefined,
    resource = undefined,
    history = undefined,
  }) {
    super(id, undefined, undefined);
    this.projectPart = projectPart ? new ProjectPart(projectPart) : null;
    this.document = document ? new FileDocument(document) : null;
    this.resource = resource ? new Resource(resource) : null;
    this.history = Array.isArray(history) ? history : [];
  }

  static get fkName() {
    return 'project_part_spec_sheet_id';
  }

  static get apiRoute() {
    return 'project_part_spec_sheet';
  }

  static get detailPageRoute() {
    return undefined;
  }

  static get listPageRoute() {
    return undefined;
  }

  get fkName() {
    return ProjectPartSpecSheet.fkName;
  }

  get apiRoute() {
    return ProjectPartSpecSheet.apiRoute;
  }

  get detailPageRoute() {
    return ProjectPartSpecSheet.detailPageRoute;
  }

  get listPageRoute() {
    return ProjectPartSpecSheet.listPageRoute;
  }

  static get joinAttrsKey() {
    return ['resource', 'document', 'history'];
  }

  static get entityListDisplayAttrs() {
    return this.getAttributes([
      'resource',
      'document',
      'projectPart',
      'history',
      'lastStatus',
    ]);
  }

  static historyEntryTime(entry) {
    const date = HitDateUtils.toDateObject(
      entry?.status_date || entry?.statusDate,
    );
    if (!date || Number.isNaN(date.getTime())) return 0;
    return date.getTime();
  }

  /**
   * Latest history entry by status_date (fallback: last array item).
   */
  static getLatestHistoryEntry(history) {
    if (!Array.isArray(history) || history.length === 0) {
      return null;
    }
    return [...history].sort(
      (a, b) => this.historyEntryTime(b) - this.historyEntryTime(a),
    )[0];
  }

  static getLatestStatusId(history) {
    const latest = this.getLatestHistoryEntry(history);
    if (!latest) return null;
    const statusId = latest.status?.id || latest.status || null;
    return typeof statusId === 'string' ? statusId.toUpperCase() : null;
  }

  static getLatestStatusEnum(history) {
    const statusId = this.getLatestStatusId(history);
    if (!statusId) return null;
    return HitOnlineEnums.getEnumValue(
      'PROJECT_PART_SPEC_SHEET_HISTORY_STATUS',
      statusId,
      translate,
    );
  }

  static getLatestStatusLabel(history) {
    const enumValue = this.getLatestStatusEnum(history);
    return enumValue?.label || this.getLatestStatusId(history) || '';
  }

  static get allAttributes() {
    return [
      new AT.FkResourceAttribute({
        name: 'resource',
        column: 'resource_id',
        joinPath: ['resource!fk_project_part_spec_sheet_resource_id_resource'],
        fkToUpdate: 'resource_id',
        tableProperties: {
          header: 'hit-base.common.resource',
          maxWidth: '2.5fr',
          readOnly: true,
        },
        readOnly: true,
      }),
      new AT.FkFileDocumentAttribute({
        name: 'document',
        column: 'document_id',
        joinPath: ['document!fk_project_part_spec_sheet_document_id_document'],
        fkToUpdate: 'document_id',
        onlyFetchDesignation: true,
        tableProperties: {
          header: 'hit-app.common.files',
          maxWidth: '1fr',
          hideByDefault: true,
          readOnly: true,
        },
        readOnly: true,
      }),
      new AT.FkProjectPartAttribute({
        name: 'projectPart',
        column: 'project_part_id',
        joinPath: [
          'project_part!fk_project_part_spec_sheet_project_part_id_project_part!inner',
        ],
        fkToUpdate: 'project_part_id',
        onlyFetchDesignation: true,
        tableProperties: {
          header: 'hit-base.common.project-part',
          maxWidth: '1fr',
          hideByDefault: true,
        },
      }),
      new AT.StatusHistoryAttribute({
        name: 'history',
        column: 'project_part_spec_sheet_history(status,status_date)',
        filterTable: 'project_part_spec_sheet_history',
        filterOptions: HitOnlineEnums.PROJECT_PART_SPEC_SHEET_HISTORY_STATUS,
        tableProperties: {
          header: 'hit-app.common.history',
          hideByDefault: true,
        },
      }),
      new AT.NotFetchedAttribute({
        name: 'lastStatus',
        forcedType: 'enum',
        transform: (item) =>
          ProjectPartSpecSheet.getLatestStatusEnum(item?.history),
        tableProperties: {
          header: 'hit-app.common.status',
          maxWidth: '1fr',
          enumType: 'PROJECT_PART_SPEC_SHEET_HISTORY_STATUS',
          staticValues: HitOnlineEnums.getEnumValues(
            'PROJECT_PART_SPEC_SHEET_HISTORY_STATUS',
            translate,
          ),
        },
      }),
    ];
  }
}
